/**
 * ui.ts — the HUD side panel and the world-painting helpers.
 *
 * Built imperatively (no framework). The panel's blocks are ordered for
 * demo-time use, top to bottom:
 *   1. Run         — Start / Stop / Reset, speed + timeout sliders, elapsed.
 *   2. Actions     — Q-value bars (32 candidates, 4 rows × 8 directions) +
 *                    the per-episode return curve.
 *   3. Reward weights    — live sliders.
 *   4. Hyperparameters   — γ, ridge λ, ε schedule, sensor P, Refit-Q-now.
 *   5. View        — belief / reveal toggles.
 *   6. Brush       — paint tools + generators + randomize + clear.
 *   7. This episode      — the live per-episode numbers.
 *   8. Run totals  — cumulative stats.
 *
 * The architectural description that used to live in the sidebar now lives
 * behind the (?) help button (see help.ts) — same pattern as the
 * fishing-demo.
 *
 * Update model: the main loop calls `panel.update(state)` once per agent
 * tick. Inputs push outward via callbacks the main loop registers.
 */

import { World } from './environment';
import { FREE, GOLD, STONE } from './belief';

export type Brush = 'gold' | 'stone' | 'eraser';

/** Snapshot the main loop pushes to the panel after each agent tick. */
export interface PanelState {
  steps: number;
  /** Episode counter (how many times env.done has fired mid-run). */
  episodes: number;
  // ── Per-episode (the prominent numbers; should change episode-to-episode
  //    as the agent learns the fixed layout). ──
  /** Gold eaten in the CURRENT (in-progress) episode. */
  episodeGoldEaten: number;
  /** Total gold in the current episode (eaten + remaining). */
  episodeGoldTotal: number;
  /** Return of the current in-progress episode. */
  episodeReturn: number;
  /** Gold eaten in the LAST COMPLETED episode (the curve plots this). */
  lastEpisodeGold: number;
  /** Return of the LAST COMPLETED episode (the curve plots this). */
  lastEpisodeReturn: number;
  // ── Cumulative totals (secondary). ──
  goldEaten: number;
  goldLeft: number;
  returnG: number;
  bumps: number;
  // ── FQI training state. ──
  /** Training round counter (how many times Q has been refit). */
  roundCount: number;
  /** ε currently in effect (per-round schedule). */
  epsilon: number;
  /** Replay buffer occupancy (transitions stored). */
  replaySize: number;
  /** Replay buffer capacity. */
  replayCapacity: number;
  /** Mean |ΔQ| from the most recent fit round (how much Q moved). */
  lastFitLoss: number;
  running: boolean;
  /** Wall-clock ms since run start. */
  elapsedMs: number;
  /** Run timeout (ms). User-editable. */
  timeoutMs: number;
  /** The 32 Q-values (8 dirs × 4 lengths), row-major length-major:
   *  index `lenIdx * 8 + dir`. */
  qValues: Float64Array;
}

export interface PanelCallbacks {
  onBrush: (b: Brush) => void;
  onClearWorld: () => void;
  onGenerate: (kind: 'spiral' | 'random' | 'empty') => void;
  /** Paint a randomised world (spiral-with-jittered-centre OR scatter with
   *  randomised stone density 10-30%). The run uses this at every episode
   *  boundary so the layout changes round-to-round — the setup doesn't
   *  matter anymore, only that the agent generalises. */
  onRandomize: () => void;
  onGamma: (v: number) => void;
  onLambda: (v: number) => void;
  onEpsPerRoundMax: (v: number) => void;
  onEpsPerRoundMin: (v: number) => void;
  onSpeed: (hz: number) => void;
  /** New run timeout in milliseconds. */
  onTimeout: (ms: number) => void;
  onSensorP: (p: number) => void;
  onRewardGold: (v: number) => void;
  onRewardBump: (v: number) => void;
  onRewardCoverage: (v: number) => void;
  onRewardStep: (v: number) => void;
  onReveal: (b: boolean) => void;
  onShowBelief: (b: boolean) => void;
  /** Trigger one fit round manually (normally this fires at episode boundaries). */
  onFitNow: () => void;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
}

const COLORS = {
  bad: '#c44e52',
  good: '#55a868',
  neutral: '#4c72b0',
  dim: '#8a9bab',
  panel: '#0f2230',
  panelLine: '#1d3a4f',
  text: '#dfe9ef',
};

/** 8 direction labels matching environment.ts DIRECTIONS order. */
const ACTION_NAMES = ['E', 'W', 'S', 'N', 'NE', 'NW', 'SE', 'SW'];
const N_DIRS = 8;
const N_LEN = 4;

/** Default run timeout (5 minutes) in ms. */
export const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

export class SidePanel {
  readonly el: HTMLElement;
  private returnCanvas: HTMLCanvasElement;
  private qbarsEl: HTMLElement;
  private statSteps: HTMLElement;
  private statEpisodes: HTMLElement;
  private statGold: HTMLElement;
  private statReturn: HTMLElement;
  private statBumps: HTMLElement;
  private statRound: HTMLElement;
  private statEps: HTMLElement;
  private statReplay: HTMLElement;
  private statFitLoss: HTMLElement;
  // Per-episode stats (the learning-is-happening numbers).
  private statEpGold: HTMLElement;
  private statEpReturn: HTMLElement;
  private statLastEp: HTMLElement;
  private statElapsed: HTMLElement;
  private statTimeout: HTMLElement;
  private btnStart: HTMLButtonElement;
  private btnStop: HTMLButtonElement;
  private btnReset: HTMLButtonElement;
  private btnFitNow: HTMLButtonElement;
  private returnHistory: number[] = [];

  constructor(cb: PanelCallbacks) {
    this.el = document.createElement('aside');
    this.el.className = 'panel';
    this.el.innerHTML = `
      <header class="panel-head">
        <h1>Gold Digger</h1>
        <p class="tagline">A linear-Q agent learns to collect gold it can only sense noisily.</p>
      </header>

      <section class="block">
        <div class="block-title">Run</div>
        <div class="btn-row">
          <button id="btn-start" class="btn btn-primary" type="button">▶ Start</button>
          <button id="btn-stop"  class="btn" type="button">■ Stop</button>
          <button id="btn-reset" class="btn" type="button">↺ Reset</button>
        </div>
        <div class="slider-row">
          <label>speed <span id="val-speed">30</span> Hz</label>
          <input type="range" id="sl-speed" min="2" max="60" step="1" value="30" />
        </div>
        <div class="slider-row">
          <label>timeout <span id="val-timeout">5:00</span></label>
          <input type="range" id="sl-timeout" min="1" max="20" step="1" value="5" />
        </div>
        <div class="stat-row"><span>elapsed</span><span id="stat-elapsed">0:00</span></div>
        <div class="stat-row"><span>timeout in</span><span id="stat-timeout">5:00</span></div>
      </section>

      <section class="block">
        <div class="block-title">Actions <span class="hint-inline">(Q-values, 4 rows × 8 dirs)</span></div>
        <div class="qbars" id="qbars"></div>
        <canvas class="chart" id="return-chart" height="70"></canvas>
        <p class="hint">Return per episode — a rising line is the agent learning.</p>
      </section>

      <section class="block">
        <div class="block-title">Reward weights <span class="hint-inline">(live — applies next step)</span></div>
        <div class="slider-row">
          <label>collect gold <span id="val-rw-gold">+1.00</span></label>
          <input type="range" id="sl-rw-gold" min="0.1" max="5" step="0.1" value="1.0" />
        </div>
        <div class="slider-row">
          <label>bump (flat) <span id="val-rw-bump">-0.30</span></label>
          <input type="range" id="sl-rw-bump" min="-2" max="0" step="0.05" value="-0.3" />
        </div>
        <div class="slider-row">
          <label>new free cell <span id="val-rw-cov">+0.15</span></label>
          <input type="range" id="sl-rw-cov" min="0" max="0.5" step="0.01" value="0.15" />
        </div>
        <div class="slider-row">
          <label>per step <span id="val-rw-step">-0.01</span></label>
          <input type="range" id="sl-rw-step" min="-0.2" max="0" step="0.005" value="-0.01" />
        </div>
        <p class="hint">If the agent ignores gold, raise <em>collect gold</em> or lower <em>new free cell</em>.</p>
      </section>

      <section class="block">
        <div class="block-title">Hyperparameters <span class="hint-inline">(apply at next fit round)</span></div>
        <div class="slider-row">
          <label>γ <span id="val-gamma">0.95</span></label>
          <input type="range" id="sl-gamma" min="0.5" max="0.99" step="0.01" value="0.95" />
        </div>
        <div class="slider-row">
          <label>ridge λ <span id="val-lambda">1.0</span></label>
          <input type="range" id="sl-lambda" min="0.1" max="10" step="0.1" value="1.0" />
        </div>
        <div class="slider-row">
          <label>ε round-1 <span id="val-eps-max">0.90</span></label>
          <input type="range" id="sl-eps-max" min="0.3" max="1" step="0.05" value="0.9" />
        </div>
        <div class="slider-row">
          <label>ε floor <span id="val-eps-min">0.15</span></label>
          <input type="range" id="sl-eps-min" min="0" max="0.5" step="0.05" value="0.15" />
        </div>
        <div class="slider-row">
          <label>sensor P(correct) <span id="val-sensor">0.85</span></label>
          <input type="range" id="sl-sensor" min="0.5" max="1" step="0.05" value="0.85" />
        </div>
        <div class="btn-row">
          <button id="btn-fit-now" class="btn btn-mini" type="button">↯ Refit Q now</button>
        </div>
      </section>

      <section class="block">
        <div class="block-title">View</div>
        <label class="check-row"><input type="checkbox" id="cb-belief" checked /> show belief heat-map</label>
        <label class="check-row"><input type="checkbox" id="cb-reveal" checked /> reveal all at full opacity</label>
      </section>

      <section class="block">
        <div class="block-title">Brush</div>
        <div class="btn-row brush-row">
          <button data-brush="gold"    class="btn brush-btn brush-gold"    type="button">Gold</button>
          <button data-brush="stone" class="btn brush-btn brush-stone" type="button">Stone</button>
          <button data-brush="eraser"  class="btn brush-btn brush-eraser"  type="button">Eraser</button>
        </div>
        <div class="btn-row">
          <button id="gen-spiral"   class="btn btn-mini" type="button">spiral</button>
          <button id="gen-random"   class="btn btn-mini" type="button">scatter</button>
          <button id="gen-empty"    class="btn btn-mini" type="button">empty</button>
          <button id="gen-clear"    class="btn btn-mini btn-warn" type="button">clear</button>
        </div>
        <p class="hint">Paint on the grid. Each episode randomises the layout (spiral centre or scatter density) — the agent must generalise, not memorise.</p>
      </section>

      <section class="block">
        <div class="block-title">This episode <span class="hint-inline">(randomised per episode)</span></div>
        <div class="stat-row big"><span>gold</span><span id="stat-ep-gold">0 / 0</span></div>
        <div class="stat-row"><span>return</span><span id="stat-ep-return">0.00</span></div>
        <div class="stat-row"><span>last episode</span><span id="stat-last-ep">— gold, — ret</span></div>
      </section>

      <section class="block">
        <div class="block-title">Run totals</div>
        <div class="stat-row"><span>episode</span><span id="stat-episodes">0</span></div>
        <div class="stat-row"><span>total gold</span><span id="stat-gold">0</span></div>
        <div class="stat-row"><span>total return</span><span id="stat-return">0.00</span></div>
        <div class="stat-row"><span>steps</span><span id="stat-steps">0</span></div>
        <div class="stat-row"><span>bumps</span><span id="stat-bumps">0</span></div>
        <div class="stat-row"><span>training round</span><span id="stat-round">0</span></div>
        <div class="stat-row"><span>ε (this round)</span><span id="stat-eps">—</span></div>
        <div class="stat-row"><span>replay buffer</span><span id="stat-replay">0</span></div>
        <div class="stat-row"><span>last |ΔQ|</span><span id="stat-fit-loss">—</span></div>
      </section>
    `;

    this.returnCanvas = this.el.querySelector<HTMLCanvasElement>('#return-chart')!;
    this.qbarsEl = this.el.querySelector<HTMLElement>('#qbars')!;
    this.statSteps = this.el.querySelector<HTMLElement>('#stat-steps')!;
    this.statEpisodes = this.el.querySelector<HTMLElement>('#stat-episodes')!;
    this.statGold = this.el.querySelector<HTMLElement>('#stat-gold')!;
    this.statReturn = this.el.querySelector<HTMLElement>('#stat-return')!;
    this.statBumps = this.el.querySelector<HTMLElement>('#stat-bumps')!;
    this.statRound = this.el.querySelector<HTMLElement>('#stat-round')!;
    this.statEps = this.el.querySelector<HTMLElement>('#stat-eps')!;
    this.statReplay = this.el.querySelector<HTMLElement>('#stat-replay')!;
    this.statFitLoss = this.el.querySelector<HTMLElement>('#stat-fit-loss')!;
    this.statEpGold = this.el.querySelector<HTMLElement>('#stat-ep-gold')!;
    this.statEpReturn = this.el.querySelector<HTMLElement>('#stat-ep-return')!;
    this.statLastEp = this.el.querySelector<HTMLElement>('#stat-last-ep')!;
    this.statElapsed = this.el.querySelector<HTMLElement>('#stat-elapsed')!;
    this.statTimeout = this.el.querySelector<HTMLElement>('#stat-timeout')!;
    this.btnStart = this.el.querySelector<HTMLButtonElement>('#btn-start')!;
    this.btnStop = this.el.querySelector<HTMLButtonElement>('#btn-stop')!;
    this.btnReset = this.el.querySelector<HTMLButtonElement>('#btn-reset')!;
    this.btnFitNow = this.el.querySelector<HTMLButtonElement>('#btn-fit-now')!;

    // Wire brush buttons.
    this.el.querySelectorAll<HTMLButtonElement>('.brush-btn').forEach((b) => {
      b.addEventListener('click', () => {
        const brush = b.dataset.brush as Brush;
        this.el
          .querySelectorAll<HTMLButtonElement>('.brush-btn')
          .forEach((x) => x.classList.remove('active'));
        b.classList.add('active');
        cb.onBrush(brush);
      });
    });
    // Default brush = gold.
    this.el.querySelector<HTMLButtonElement>('.brush-gold')!.classList.add('active');

    this.el.querySelector<HTMLButtonElement>('#gen-spiral')!.addEventListener('click', () => cb.onGenerate('spiral'));
    this.el.querySelector<HTMLButtonElement>('#gen-random')!.addEventListener('click', () => cb.onGenerate('random'));
    this.el.querySelector<HTMLButtonElement>('#gen-empty')!.addEventListener('click', () => cb.onGenerate('empty'));
    this.el.querySelector<HTMLButtonElement>('#gen-clear')!.addEventListener('click', () => cb.onClearWorld());

    this.btnStart.addEventListener('click', () => cb.onStart());
    this.btnStop.addEventListener('click', () => cb.onStop());
    this.btnReset.addEventListener('click', () => cb.onReset());
    this.btnFitNow.addEventListener('click', () => cb.onFitNow());

    this.wireSlider('sl-gamma', 'val-gamma', (v) => v.toFixed(2), cb.onGamma);
    this.wireSlider('sl-lambda', 'val-lambda', (v) => v.toFixed(1), cb.onLambda);
    this.wireSlider('sl-eps-max', 'val-eps-max', (v) => v.toFixed(2), cb.onEpsPerRoundMax);
    this.wireSlider('sl-eps-min', 'val-eps-min', (v) => v.toFixed(2), cb.onEpsPerRoundMin);
    this.wireSlider('sl-speed', 'val-speed', (v) => v.toFixed(0), cb.onSpeed);
    this.wireSlider('sl-sensor', 'val-sensor', (v) => v.toFixed(2), cb.onSensorP);
    // Timeout slider is integer minutes → ms.
    this.wireSlider('sl-timeout', 'val-timeout', (v) => `${v.toFixed(0)}:00`, (min) =>
      cb.onTimeout(Math.max(1, Math.round(min)) * 60 * 1000),
    );
    // Reward weights — signed, so the value label shows the sign explicitly.
    this.wireSlider('sl-rw-gold', 'val-rw-gold', (v) => (v >= 0 ? '+' : '') + v.toFixed(2), cb.onRewardGold);
    this.wireSlider('sl-rw-bump', 'val-rw-bump', (v) => (v >= 0 ? '+' : '') + v.toFixed(2), cb.onRewardBump);
    this.wireSlider('sl-rw-cov', 'val-rw-cov', (v) => (v >= 0 ? '+' : '') + v.toFixed(2), cb.onRewardCoverage);
    this.wireSlider('sl-rw-step', 'val-rw-step', (v) => (v >= 0 ? '+' : '') + v.toFixed(2), cb.onRewardStep);

    this.el.querySelector<HTMLInputElement>('#cb-belief')!.addEventListener('change', (e) => {
      cb.onShowBelief((e.target as HTMLInputElement).checked);
    });
    this.el.querySelector<HTMLInputElement>('#cb-reveal')!.addEventListener('change', (e) => {
      cb.onReveal((e.target as HTMLInputElement).checked);
    });

    // Seed the 32 action-bar cells: 4 rows × 8 directions. Each cell is a
    // small vertical bar with the direction label underneath. Rows are
    // labelled by leap length (1, 2, 4, 8).
    for (let lenIdx = 0; lenIdx < N_LEN; lenIdx++) {
      const row = document.createElement('div');
      row.className = 'qbar-row';
      const lenLabel = document.createElement('span');
      lenLabel.className = 'qbar-len';
      lenLabel.textContent = String([1, 2, 4, 8][lenIdx]);
      row.appendChild(lenLabel);
      const grid = document.createElement('div');
      grid.className = 'qbar-grid';
      for (let d = 0; d < N_DIRS; d++) {
        const cell = document.createElement('div');
        cell.className = 'qbar-cell';
        cell.innerHTML = `<div class="qbar"><div class="qbar-fill"></div></div><span class="qbar-name">${ACTION_NAMES[d]}</span>`;
        grid.appendChild(cell);
      }
      row.appendChild(grid);
      this.qbarsEl.appendChild(row);
    }
  }

  private wireSlider(
    inputId: string,
    valId: string,
    fmt: (v: number) => string,
    onChange: (v: number) => void,
  ): void {
    const input = this.el.querySelector<HTMLInputElement>(`#${inputId}`)!;
    const val = this.el.querySelector<HTMLElement>(`#${valId}`)!;
    val.textContent = fmt(parseFloat(input.value));
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      val.textContent = fmt(v);
      onChange(v);
    });
  }

  /** Called once per agent tick with the latest snapshot. */
  update(s: PanelState): void {
    // Per-episode (prominent).
    this.statEpGold.textContent = `${s.episodeGoldEaten} / ${s.episodeGoldTotal}`;
    this.statEpReturn.textContent = s.episodeReturn.toFixed(2);
    if (s.episodes > 0) {
      this.statLastEp.textContent = `${s.lastEpisodeGold} gold, ${s.lastEpisodeReturn.toFixed(1)} ret`;
    } else {
      this.statLastEp.textContent = '— (first episode)';
    }
    // Cumulative totals.
    this.statSteps.textContent = String(s.steps);
    this.statEpisodes.textContent = String(s.episodes);
    this.statGold.textContent = String(s.goldEaten);
    this.statReturn.textContent = s.returnG.toFixed(2);
    this.statBumps.textContent = String(s.bumps);
    // FQI state.
    this.statRound.textContent = String(s.roundCount);
    this.statEps.textContent = Number.isNaN(s.epsilon) ? '—' : s.epsilon.toFixed(3);
    this.statReplay.textContent = `${s.replaySize} / ${s.replayCapacity}`;
    this.statFitLoss.textContent = s.lastFitLoss >= 0 ? s.lastFitLoss.toFixed(4) : '—';
    this.statElapsed.textContent = fmtElapsed(s.elapsedMs);
    this.statTimeout.textContent = fmtElapsed(Math.max(0, s.timeoutMs - s.elapsedMs));

    // Action bars — 32 Q-values laid out 4×8.
    if (s.qValues.length === N_DIRS * N_LEN) {
      // Normalise to [0, 1] across ALL 32 (so cross-length comparison is
      // honest: the viewer sees which (length, direction) pair the agent
      // currently ranks highest overall).
      let lo = Infinity;
      let hi = -Infinity;
      for (let i = 0; i < s.qValues.length; i++) {
        const v = s.qValues[i];
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
      const range = hi - lo > 1e-6 ? hi - lo : 1;
      const rows = this.qbarsEl.children;
      for (let lenIdx = 0; lenIdx < N_LEN; lenIdx++) {
        const row = rows[lenIdx] as HTMLElement;
        const grid = row.querySelector<HTMLElement>('.qbar-grid')!;
        const cells = grid.children;
        for (let d = 0; d < N_DIRS; d++) {
          const cell = cells[d] as HTMLElement;
          const fill = cell.querySelector<HTMLElement>('.qbar-fill')!;
          const q = s.qValues[lenIdx * N_DIRS + d];
          const mag = (q - lo) / range;
          fill.style.height = `${Math.max(2, mag * 100)}%`;
        }
      }
    }

    // Per-episode return curve. Push ONE dot per completed episode.
    if (s.episodes > this.returnHistory.length) {
      this.returnHistory.push(s.lastEpisodeReturn);
      if (this.returnHistory.length > 400) this.returnHistory.shift();
    }
    drawLineChart(this.returnCanvas, this.returnHistory, COLORS.good);
  }

  /** Reset the return curve (called on start/reset). */
  resetCharts(): void {
    this.returnHistory = [];
    const ctx = this.returnCanvas.getContext('2d')!;
    ctx.clearRect(0, 0, this.returnCanvas.width, this.returnCanvas.height);
  }
}

/** Format milliseconds as m:ss. */
function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Tiny line chart on a canvas. */
function drawLineChart(canvas: HTMLCanvasElement, ys: number[], color: string): void {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 200;
  const cssH = canvas.clientHeight || 70;
  if (canvas.width !== Math.floor(cssW * dpr)) {
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
  }
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  if (ys.length < 2) return;
  let lo = Math.min(...ys);
  let hi = Math.max(...ys);
  if (hi - lo < 1e-6) hi = lo + 1;
  const x = (i: number) => (i / (ys.length - 1)) * cssW;
  const y = (v: number) => cssH - ((v - lo) / (hi - lo)) * (cssH - 4) - 2;

  ctx.strokeStyle = COLORS.panelLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, y(lo));
  ctx.lineTo(cssW, y(lo));
  ctx.stroke();

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  for (let i = 0; i < ys.length; i++) {
    if (i === 0) ctx.moveTo(x(i), y(ys[i]));
    else ctx.lineTo(x(i), y(ys[i]));
  }
  ctx.stroke();
  ctx.lineTo(x(ys.length - 1), y(lo));
  ctx.lineTo(x(0), y(lo));
  ctx.closePath();
  ctx.fillStyle = color + '22';
  ctx.fill();
}

// ── World generators ──────────────────────────────────────────────────────

/** The default spiral's arm spacing (cells between successive turns).
 *
 *  Tuned for visual clarity: at 3.0 the arms are clearly distinct paths with
 *  readable gaps between turns. */
const SPIRAL_ARM_SPACING = 3.0;

/** Paint an Archimedean spiral of gold on the world, then sprinkle stones
 *  across the whole grid at the requested density. This is the requirement
 *  (3) showpiece: "follow the spiral, avoid the stones".
 *
 *  Parameterised by arm spacing (cells between turns) rather than the raw
 *  `a` coefficient, because spacing is the visible quantity. The arc-length
 *  step uses the true derivative `dθ = ds / sqrt(r² + (aθ)²)` so successive
 *  gold cells land ~1 cell apart regardless of how tight the spiral is.
 *
 *  `cx`/`cy` are the spiral centre in cell coords. Default = grid centre.
 *  The run loop passes a randomised centre per episode so the spiral doesn't
 *  always live in the same spot.
 *
 *  `stoneP` is the fraction of cells turned into stones (randomised 10–30%
 *  by default, matching scatter). Stones are scattered AFTER the spiral is
 *  painted and never overwrite gold, so the spiral arm stays intact while
 *  the surrounding space carries realistic obstacle density. The agent's
 *  starting patch is cleared. */
export function generateSpiral(
  world: World,
  opts?: { cx?: number; cy?: number; stoneP?: number; rng?: () => number },
): void {
  const g = world.grid;
  const cells = new Uint8Array(g * g);
  const cx = opts?.cx ?? (g - 1) / 2;
  const cy = opts?.cy ?? (g - 1) / 2;
  const rng = opts?.rng ?? Math.random;
  // Shrink maxR when the centre is off-centre so the spiral stays in bounds.
  const margin = Math.min(cx, cy, g - 1 - cx, g - 1 - cy);
  const maxR = Math.min(g * 0.42, margin + 0.5);
  if (maxR < 2) {
    // Centre too close to a wall — just paint a small gold cluster and bail.
    if (g > 0) cells[Math.round(cy) * g + Math.round(cx)] = GOLD;
    world.loadFrom(cells);
    return;
  }
  const a = SPIRAL_ARM_SPACING / (2 * Math.PI);
  let theta = Math.PI / 2;
  let prevCi = -1;
  let prevCj = -1;
  while (true) {
    const r = a * theta;
    if (r > maxR) break;
    const x = cx + r * Math.cos(theta);
    const y = cy + r * Math.sin(theta);
    const ci = Math.round(x);
    const cj = Math.round(y);
    if (
      ci >= 0 && ci < g && cj >= 0 && cj < g &&
      !(ci === prevCi && cj === prevCj)
    ) {
      cells[cj * g + ci] = GOLD;
      prevCi = ci;
      prevCj = cj;
    }
    const ds = 1;
    theta += ds / Math.sqrt(r * r + a * a);
  }
  // Clear the centre cell.
  const cc = Math.round(cx);
  const cr = Math.round(cy);
  if (cr >= 0 && cr < g && cc >= 0 && cc < g) cells[cr * g + cc] = FREE;

  // Sprinkle stones across the whole grid at the requested density. Skip
  // GOLD cells (keep the spiral arm intact) and the agent's starting patch.
  const stoneP = opts?.stoneP ?? 0.05 + rng() * 0.10; // 5%–15%
  const startR = Math.floor(g / 2);
  const startC = Math.floor(g / 2);
  for (let r = 0; r < g; r++) {
    for (let c = 0; c < g; c++) {
      if (cells[r * g + c] === GOLD) continue;
      if (Math.abs(r - startR) <= 1 && Math.abs(c - startC) <= 1) continue;
      if (rng() < stoneP) cells[r * g + c] = STONE;
    }
  }
  world.loadFrom(cells);
}

/** Scatter random gold and stones. Stone density is randomised in [0.05, 0.15]
 *  so the agent sees a range of obstacle densities round-to-round. Clear a
 *  starting patch at the centre so the agent (which starts at the centre) has
 *  room. */
export function generateRandom(
  world: World,
  rng: () => number,
  opts?: { goldP?: number; stoneP?: number },
): void {
  const g = world.grid;
  const cells = new Uint8Array(g * g);
  const goldP = opts?.goldP ?? 0.18;
  const stoneP = opts?.stoneP ?? 0.05 + rng() * 0.10; // 5%–15%
  for (let i = 0; i < cells.length; i++) {
    const u = rng();
    if (u < goldP) cells[i] = GOLD;
    else if (u < goldP + stoneP) cells[i] = STONE;
  }
  const cc = Math.floor(g / 2);
  const cr = Math.floor(g / 2);
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const r = cr + dr;
      const c = cc + dc;
      if (r >= 0 && r < g && c >= 0 && c < g) cells[r * g + c] = FREE;
    }
  }
  world.loadFrom(cells);
}

/** Empty the world (FREE everywhere). */
export function generateEmpty(world: World): void {
  world.loadFrom(new Uint8Array(world.grid * world.grid));
}

/** Randomise the world: ~half the time a spiral (with a randomised centre
 *  AND randomised stone density), ~half the time a scatter (with a randomised
 *  stone density). The agent must GENERALISE — it cannot memorise a fixed
 *  layout because there is no fixed layout.
 *
 *  Used by the run loop at every episode boundary. */
export function randomizeWorld(world: World, rng: () => number): void {
  if (rng() < 0.5) {
    // Spiral with a jittered centre. Keep the centre at least 5 cells from
    // any wall so the spiral has room to wind.
    const g = world.grid;
    const lo = 5;
    const hi = g - 6;
    const cx = lo + Math.floor(rng() * (hi - lo + 1));
    const cy = lo + Math.floor(rng() * (hi - lo + 1));
    generateSpiral(world, { cx, cy, rng });
  } else {
    generateRandom(world, rng);
  }
}
